import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import { movies } from "@/mocks/movies";
import type { Movie, MovieType } from "@/types/movie";
import {
  MOVIE_GENRES,
  MOVIE_REGIONS,
  MOVIE_YEARS,
  filterMovies,
  paginate,
  type SortKey,
} from "@/utils/movieFilters";

const PAGE_SIZE = 12;

const channelMeta: Record<MovieType, { title: string; desc: string; crumb: string }> = {
  movie: {
    title: "电影频道",
    desc: "海量高清电影免费看，动作、科幻、悬疑、剧情应有尽有。",
    crumb: "电影",
  },
  tv: {
    title: "电视剧频道",
    desc: "追更热播剧集，古装权谋、都市情感、刑侦悬疑一网打尽。",
    crumb: "电视剧",
  },
};

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新更新" },
  { key: "views", label: "播放量" },
  { key: "rating", label: "评分" },
];

interface ChipGroupProps {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}

function ChipGroup({ label, options, value, onChange }: ChipGroupProps) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-14 shrink-0 pt-1.5 text-xs md:text-sm text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`h-8 px-3 rounded-md text-xs md:text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
              value === opt
                ? "bg-primary-500 text-foreground-950"
                : "bg-background-100 border border-background-200 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const channel: MovieType = type === "tv" ? "tv" : "movie";
  const meta = channelMeta[channel];

  const allMovies = movies as Movie[];
  const region = searchParams.get("region") ?? "全部";
  const year = searchParams.get("year") ?? "全部";
  const genre = searchParams.get("genre") ?? "全部";
  const sort = (searchParams.get("sort") as SortKey) ?? "latest";
  const page = Number(searchParams.get("page") ?? "1") || 1;

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, val]) => {
      if (val === null || val === "全部" || val === "") {
        next.delete(key);
      } else {
        next.set(key, val);
      }
    });
    setSearchParams(next);
  };

  const setFilter = (key: string, value: string) => {
    updateParams({ [key]: value, page: null });
  };

  const resetFilters = () => setSearchParams(new URLSearchParams());

  const filtered = useMemo(
    () => filterMovies(allMovies, { type: channel, region, year, genre, sort }),
    [allMovies, channel, region, year, genre, sort]
  );

  const { items, total, totalPages, page: safePage } = useMemo(
    () => paginate(filtered, page, PAGE_SIZE),
    [filtered, page]
  );

  const hasFilter = region !== "全部" || year !== "全部" || genre !== "全部";

  const filterContent = (
    <div className="space-y-4">
      <ChipGroup label="地区" options={MOVIE_REGIONS} value={region} onChange={(v) => setFilter("region", v)} />
      <ChipGroup label="年份" options={MOVIE_YEARS} value={year} onChange={(v) => setFilter("year", v)} />
      <ChipGroup label="类型" options={MOVIE_GENRES} value={genre} onChange={(v) => setFilter("genre", v)} />
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8 lg:px-12 pb-16 md:pb-24">
      <div className="pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: meta.crumb }]} />
      </div>

      <header className="pt-5 md:pt-7">
        <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
          {meta.title}
          <span className="ml-2 text-base md:text-xl font-normal text-foreground-500">
            · 91在线看高清在线播放
          </span>
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-7">
          {meta.desc}
        </p>
      </header>

      {/* 工具栏 */}
      <div className="mt-6 md:mt-8 rounded-lg bg-background-100 border border-background-200/70 p-4 md:p-5">
        <div className="hidden md:block">{filterContent}</div>

        <div className="flex md:hidden items-center gap-2">
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            className="flex-1 h-10 rounded-md bg-background-200 text-foreground-900 text-sm font-label flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-filter-3-line text-lg"></i>
            筛选条件
            {hasFilter && <span className="w-2 h-2 rounded-full bg-accent-500"></span>}
          </button>
          {hasFilter && (
            <button
              type="button"
              onClick={resetFilters}
              className="h-10 px-4 rounded-md border border-background-300 text-foreground-700 text-sm whitespace-nowrap cursor-pointer"
            >
              重置
            </button>
          )}
        </div>

        <div className="mt-4 md:mt-5 pt-4 md:pt-5 border-t border-background-200/70 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="text-xs md:text-sm text-foreground-500 hidden md:inline">排序</span>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-50 border border-background-200">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setFilter("sort", opt.key)}
                  className={`h-8 px-3.5 rounded-full text-xs md:text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                    sort === opt.key
                      ? "bg-primary-500 text-foreground-950"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs md:text-sm text-foreground-500">
            共 <span className="text-accent-400 font-semibold">{total}</span> 部影视资源
          </p>
        </div>
      </div>

      {/* 列表 */}
      {items.length > 0 ? (
        <div className="mt-8 md:mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-4 gap-y-7 md:gap-x-5">
          {items.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      ) : (
        <div className="mt-10 md:mt-16 rounded-lg bg-background-100 border border-dashed border-background-300 py-16 md:py-20 flex flex-col items-center text-center px-6">
          <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-500">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h2 className="mt-5 font-heading text-lg md:text-xl font-bold text-foreground-900">
            暂无符合条件的影视资源
          </h2>
          <p className="mt-2 text-sm text-foreground-500 max-w-md">
            试试更换地区、年份或类型筛选条件，或一键重置查看全部内容。
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-6 h-10 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-label whitespace-nowrap transition-colors cursor-pointer"
          >
            重置筛选
          </button>
        </div>
      )}

      <Pagination
        page={safePage}
        totalPages={totalPages}
        onChange={(p) => updateParams({ page: p === 1 ? null : String(p) })}
      />

      {/* 移动端全屏筛选面板 */}
      {filterOpen && (
        <div className="fixed inset-0 z-[90] bg-background-50 md:hidden flex flex-col animate-fade-in">
          <div className="flex items-center justify-between h-16 px-4 border-b border-background-200">
            <h2 className="font-heading text-lg font-bold text-foreground-950">筛选条件</h2>
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-600 cursor-pointer"
              aria-label="关闭筛选"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-5">{filterContent}</div>
          <div className="flex items-center gap-3 px-4 py-4 border-t border-background-200">
            <button
              type="button"
              onClick={resetFilters}
              className="flex-1 h-11 rounded-md border border-background-300 text-foreground-700 text-sm whitespace-nowrap cursor-pointer"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="flex-1 h-11 rounded-md bg-primary-500 text-foreground-950 text-sm font-label whitespace-nowrap cursor-pointer"
            >
              确定
            </button>
          </div>
        </div>
      )}
    </div>
  );
}